import React from 'react';
import { Button } from '../components/ui/button';
import { Check, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ContactFormBlefaro from '../components/ContactFormBlefaro';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const Blefaroplastia = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#B8975A] to-[#9d6f5a]"></div>
        
        <div className="container mx-auto max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-8">
            <a href="tel:952764547" className="text-[#B8975A] font-semibold hover:underline">
              {t('common.phone')}
            </a>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1C1C1A] text-center mb-6 leading-tight">
              {t('pages.blefaroplastia.heroTitle')}
            </h1>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <p className="text-lg md:text-xl text-text-secondary text-center mb-4 max-w-3xl mx-auto">
              {t('pages.blefaroplastia.heroSubtitle')}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.3}>
            <p className="text-lg text-text-secondary text-center mb-8">
              {t('pages.blefaroplastia.bookConsult')}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.4} className="text-center">
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {t('pages.blefaroplastia.heroCta')}
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Before/After Slider */}
      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-8">
                {t('pages.blefaroplastia.realResults')}
              </h2>
            </AnimatedSection>
            <div className="space-y-12">
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <BeforeAfterSlider 
                  beforeImage="/images/blefaroplastia-antes.jpg"
                  afterImage="/images/blefaroplastia-despues.jpg"
                  beforeLabel={t('common.before')}
                  afterLabel={t('common.after')}
                  className="mx-auto"
                />
                <p className="text-center text-text-secondary text-sm mt-3 font-medium">
                  {t('pages.blefaroplastia.upperAndLower')}
                </p>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.2}>
                <BeforeAfterSlider 
                  beforeImage="/images/blefaro-superior-antes.jpg"
                  afterImage="/images/blefaro-superior-despues.jpg"
                  beforeLabel={t('common.before')}
                  afterLabel={t('common.after')}
                  className="mx-auto"
                />
                <p className="text-center text-text-secondary text-sm mt-3 font-medium">
                  {t('pages.blefaroplastia.upperOnly')}
                </p>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.3}>
                <BeforeAfterSlider 
                  beforeImage="/images/blefaro3-antes.jpg"
                  afterImage="/images/blefaro3-despues.jpg"
                  beforeLabel={t('common.before')}
                  afterLabel={t('common.fifteenDays')}
                  className="mx-auto"
                />
                <p className="text-center text-text-secondary text-sm mt-3 font-medium">
                  {t('pages.blefaroplastia.upperNeuro')}
                </p>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.4}>
                <BeforeAfterSlider 
                  beforeImage="/images/blefaroplastia-rejuvenecimiento-pre.jpg"
                  afterImage="/images/blefaroplastia-rejuvenecimiento-post.jpg"
                  beforeLabel={t('common.before')}
                  afterLabel={t('common.after')}
                  className="mx-auto"
                />
                <p className="text-center text-text-secondary text-sm mt-3 font-medium">
                  {t('pages.blefaroplastia.plusRejuvenation')}
                </p>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.5}>
                <BeforeAfterSlider 
                  beforeImage="/images/blefaroplastia-bolsas-antes.png"
                  afterImage="/images/blefaroplastia-bolsas-despues.png"
                  beforeLabel={t('common.before')}
                  afterLabel={t('common.after')}
                  className="mx-auto"
                />
                <p className="text-center text-text-secondary text-sm mt-3 font-medium">
                  {t('pages.blefaroplastia.bagsNewlip')}
                </p>
              </AnimatedSection>
            </div>
            <p className="text-center text-text-muted text-sm mt-8">
              {t('common.realCases')}
            </p>
          </div>
        </div>
      </section>

      {/* Signs Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">
              {t('pages.blefaroplastia.signsTitle')}
            </h2>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <ul className="space-y-4 mb-12">
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.sign1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.sign2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.sign3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.sign4')}</span>
              </li>
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-cream px-4">
        <div className="container mx-auto max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-4">
              {t('pages.blefaroplastia.benefitsTitle')}
            </h2>
            
            <p className="text-lg text-text-secondary mb-6">
              <strong>{t('pages.blefaroplastia.benefitsSubtitle')}</strong>
            </p>
            
            <p className="text-lg text-text-secondary mb-8">
              {t('pages.blefaroplastia.benefitsDesc')}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.benefit1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.benefit2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.benefit3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t('pages.blefaroplastia.benefit4')}</span>
              </li>
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ Quick Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AnimatedSection animation="fadeUp" delay={0}>
              <div className="bg-ivory p-6 rounded-none shadow-md h-full">
                <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">
                  {t('pages.blefaroplastia.faq1Title')}
                </h3>
                <p className="text-text-secondary">
                  {t('pages.blefaroplastia.faq1Answer')}
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="bg-ivory p-6 rounded-none shadow-md h-full">
                <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">
                  {t('pages.blefaroplastia.faq2Title')}
                </h3>
                <p className="text-text-secondary">
                  {t('pages.blefaroplastia.faq2Answer')}
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="bg-ivory p-6 rounded-none shadow-md h-full">
                <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">
                  {t('pages.blefaroplastia.faq3Title')}
                </h3>
                <p className="text-text-secondary">
                  {t('pages.blefaroplastia.faq3Answer')}
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div className="bg-ivory p-6 rounded-none shadow-md h-full">
                <h3 className="text-xl font-semibold text-[#1C1C1A] mb-3">
                  {t('pages.blefaroplastia.faq4Title')}
                </h3>
                <p className="text-text-secondary">
                  {t('pages.blefaroplastia.faq4Answer')}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1C1C1A] text-white text-center px-4">
        <div className="container mx-auto max-w-3xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              {t('pages.blefaroplastia.ctaTitle')}
            </h2>
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-ivory text-[#1C1C1A] hover:bg-gray-100 px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {t('common.requestAssessment')}
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Guía de Cuidados CTA */}
      <section className="py-20 px-4 bg-cream">
        <div className="container mx-auto max-w-4xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <p className="eyebrow mb-4">{t('pages.blefaroplastia.guideTitle')}</p>
            <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-4">
              {t('pages.blefaroplastia.guideTitle')}
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-6"></div>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('pages.blefaroplastia.guideSubtitle')}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <AnimatedSection animation="fadeLeft">
              <Card className="p-6 bg-ivory border-l-4 border-gold h-full">
                <h3 className="font-serif text-xl text-charcoal mb-3">{t('pages.blefaroplastia.beforeProcedure')}</h3>
                <ul className="space-y-2 text-text-secondary text-sm">
                  <li>• Valoración del ojo seco</li>
                  <li>• Medicación a suspender</li>
                  <li>• Ayuno según tipo de anestesia</li>
                  <li>• Qué llevar el día de la cirugía</li>
                </ul>
              </Card>
            </AnimatedSection>
            <AnimatedSection animation="fadeRight">
              <Card className="p-6 bg-ivory border-l-4 border-gold h-full">
                <h3 className="font-serif text-xl text-charcoal mb-3">{t('pages.blefaroplastia.afterProcedure')}</h3>
                <ul className="space-y-2 text-text-secondary text-sm">
                  <li>• Cuidados primeras 48 horas</li>
                  <li>• Aplicación de frío y postura</li>
                  <li>• Medicación y seguimiento</li>
                  <li>• Cuidado de la cicatriz</li>
                </ul>
              </Card>
            </AnimatedSection>
          </div>

          <AnimatedSection animation="fadeUp" className="text-center">
            <Button
              onClick={() => navigate('/guia-cuidados-blefaroplastia')}
              className="bg-gold hover:bg-gold/90 text-white px-8 py-6 text-lg"
            >
              <FileText size={20} className="mr-2" />
              {t('pages.blefaroplastia.viewGuide')}
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <AnimatedSection animation="fadeLeft" className="lg:w-1/3">
              <img 
                src="/images/foto-angel-bata.webp"
                alt="Dr. Ángel Norro"
                className="w-full h-auto rounded-none shadow-lg"
              />
            </AnimatedSection>
            
            <AnimatedSection animation="fadeRight" className="lg:w-2/3">
              <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">
                {t('pages.blefaroplastia.doctorSection')}
              </h2>
              
              <p className="text-text-secondary mb-4">
                Soy el doctor Ángel Norro, <strong>cirujano</strong> especialista en blefaroplastia y miembro del SECE.
              </p>
              
              <p className="text-text-secondary mb-4">
                A lo largo de mis 30 años de experiencia y tras más de <strong>5000 intervenciones</strong>, no he perdido la ilusión por devolverle a las personas su atractivo.
              </p>
              
              <p className="text-text-secondary">
                Mi filosofía se basa en la <strong>profesionalidad</strong> y el respeto a cada uno de mis pacientes para mejorar su <strong>calidad de vida</strong>.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="py-20 bg-cream px-4">
        <div className="container mx-auto max-w-2xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6 text-center">
              {t('pages.blefaroplastia.contactTitle')}
            </h2>
            
            <p className="text-lg text-text-secondary mb-4 text-center">
              <strong>{t('pages.blefaroplastia.contactSubtitle')}</strong>
            </p>
            
            <p className="text-text-secondary mb-8 text-center">
              {t('pages.blefaroplastia.contactInfo')}
            </p>
          </AnimatedSection>
          
          <ContactFormBlefaro />
        </div>
      </section>

      {/* Benefits List */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8 text-center">
              {t('pages.blefaroplastia.wellbeingTitle')}
            </h2>
            
            <p className="text-lg text-text-secondary mb-8 text-center">
              {t('pages.blefaroplastia.wellbeingSubtitle')}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={24} />
                <span className="text-text-secondary"><strong>Atención médica</strong> cualificada y transparente.</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={24} />
                <span className="text-text-secondary">Primera cita con <strong>diseño exclusivo de tu mirada</strong>.</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={24} />
                <span className="text-text-secondary">Intervención <strong>indolora, rápida y sin ingreso</strong>.</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={24} />
                <span className="text-text-secondary">Instalaciones modernas de <strong>máximo confort</strong>.</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={24} />
                <span className="text-text-secondary">Seguimiento en <strong>consulta presencial y videoconsulta</strong>.</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="text-[#B8975A] mt-1 flex-shrink-0" size={24} />
                <span className="text-text-secondary"><strong>Resultado natural sin cicatrices visibles</strong>.</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Price Info */}
      <section className="py-20 bg-cream px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <AnimatedSection animation="fadeUp">
            <h3 className="text-2xl font-light text-[#1C1C1A] mb-4">
              {t('pages.blefaroplastia.priceTitle')}
            </h3>
            <p className="text-xl text-text-secondary mb-4">
              <strong>{t('pages.blefaroplastia.priceSubtitle')}</strong>
            </p>
            <p className="text-lg text-text-secondary mb-8">
              {t('pages.blefaroplastia.priceInfo')}
            </p>
            
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {t('cta.requestAssessment')}
            </Button>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Blefaroplastia;
